/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { getStylist } from 'src/core/themes/luisStylist';

export type TruncatedTextProps = LuisTextProps & {
    /**
     * The maximum width of the text before it gets truncated.
     */
    maxWidth?: number | string;
    /**
     * The minimum width of the text before it gets truncated.
     */
    minWidth?: number | string;

    /**
     * The HTML DOM title attribute.
     */
    title?: string;
};

const { styleComponent } = getStylist('TruncatedText');

const TruncatedLuisText = styleComponent<LuisTextProps>(LuisText)<{ maxWidth: number | string; minWidth: number | string }>(
    'TruncatedLuisText',
    {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    ({ customProps: { maxWidth, minWidth } }) => ({ maxWidth, minWidth })
);

/**
 * A presentational component that supports truncating text when it exceeds certain threshold.
 */
export const TruncatedText = (props: React.PropsWithChildren<TruncatedTextProps>) => (
    <TruncatedLuisText {...props} title={props.title} customProps={{ maxWidth: props.maxWidth || 240, minWidth: props.minWidth || 0 }}>
        {props.children}
    </TruncatedLuisText>
);
